import React, { useState, useEffect } from 'react';

const Settings = ({ theme: globalTheme, onThemeChange, onWallpaperChange, initialSection }) => {
  const [activeSection, setActiveSection] = useState(initialSection || 'display');
  const [settings, setSettings] = useState({
    display: {
      brightness: 80,
      darkMode: globalTheme === 'dark',
      fontSize: 'medium',
      animation: true
    },
    personalize: {
      accentColor: '#4a90e2',
      wallpaper: 'default',
      transparencyEffects: true,
      roundedCorners: true
    },
    privacy: {
      locationServices: false,
      collectData: false,
      saveHistory: true
    },
    about: {
      version: '1.0.0',
      buildNumber: '2023.05.15',
      deviceName: 'Web Browser',
      storage: {
        used: '250 MB',
        total: '1 GB'
      }
    }
  });

  // Synchronize settings with localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('webTopOS_settings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Save settings when they change
  useEffect(() => {
    localStorage.setItem('webTopOS_settings', JSON.stringify(settings));
    
    // If dark mode setting changed, update the global theme
    if (onThemeChange && (settings.display.darkMode ? 'dark' : 'light') !== globalTheme) {
      onThemeChange(settings.display.darkMode ? 'dark' : 'light');
    }
  }, [settings, onThemeChange, globalTheme]);

  // Update active section when initialSection prop changes
  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection);
    }
  }, [initialSection]);

  const handleDisplaySettings = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      display: {
        ...prev.display,
        [setting]: value
      }
    }));
  };

  const handlePersonalizeSettings = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      personalize: {
        ...prev.personalize,
        [setting]: value
      }
    }));

    // If wallpaper was changed, notify parent component
    if (setting === 'wallpaper' && onWallpaperChange) {
      const selectedWallpaper = wallpaperOptions.find(wp => wp.id === value);
      if (selectedWallpaper) {
        onWallpaperChange(selectedWallpaper.path);
      }
    }
  };

  // Sidebar menu items
  const menuItems = [
    { id: 'display', label: 'Display', icon: 'desktop' },
    { id: 'personalize', label: 'Personalize', icon: 'paint-brush' },
    { id: 'privacy', label: 'Privacy', icon: 'shield-alt' },
    { id: 'about', label: 'About', icon: 'info-circle' }
  ];

  // Wallpaper options with actual wallpapers from the system
  const wallpaperOptions = [
    { id: 'default', name: 'Default', thumbnail: '/assets/wallpapers/default-thumb.jpg', path: '/assets/wallpapers/default.jpg' },
    { id: 'custom', name: 'Custom', thumbnail: '/assets/wallpapers/custom.jpg', path: '/assets/wallpapers/custom.jpg' },
    { id: 'custom2', name: 'Custom 2', thumbnail: '/assets/wallpapers/custom2.png', path: '/assets/wallpapers/custom2.png' },
    { id: '480538', name: 'Abstract', thumbnail: '/assets/wallpapers/480538.png', path: '/assets/wallpapers/480538.png' },
    { id: 'thumb-1920-1276648', name: 'Space', thumbnail: '/assets/wallpapers/thumb-1920-1276648.png', path: '/assets/wallpapers/thumb-1920-1276648.png' }
  ];

  // Color options
  const colorOptions = [
    { id: '#4a90e2', color: '#4a90e2' },
    { id: '#50e3c2', color: '#50e3c2' },
    { id: '#ff5e3a', color: '#ff5e3a' },
    { id: '#ff9500', color: '#ff9500' },
    { id: '#bd10e0', color: '#bd10e0' },
    { id: '#7ed321', color: '#7ed321' }
  ];

  // Font size options
  const fontSizeOptions = [
    { id: 'small', label: 'Small' },
    { id: 'medium', label: 'Medium' },
    { id: 'large', label: 'Large' }
  ];

  const renderDisplaySettings = () => (
    <div className="settings-content">
      <h2>Display Settings</h2>
      <p>Customize how your screen looks and behaves</p>
      
      <div className="settings-row">
        <div className="setting-label">Brightness</div>
        <div className="setting-control">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <i className="fas fa-sun" style={{ color: '#aaa', fontSize: '16px' }}></i>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={settings.display.brightness} 
              onChange={(e) => handleDisplaySettings('brightness', parseInt(e.target.value))}
              style={{
                width: '200px',
                accentColor: settings.personalize.accentColor
              }}
            />
            <span style={{ color: '#aaa', fontSize: '14px' }}>{settings.display.brightness}%</span>
          </div>
        </div>
      </div>
      
      <div className="settings-row">
        <div className="setting-label">Dark Mode</div>
        <div className="setting-control">
          <label className="toggle-switch">
            <input 
              type="checkbox" 
              checked={settings.display.darkMode} 
              onChange={(e) => handleDisplaySettings('darkMode', e.target.checked)}
            />
            <span className="toggle-slider" style={{ backgroundColor: settings.display.darkMode ? settings.personalize.accentColor : '#666' }}></span>
          </label>
        </div>
      </div>
      
      <div className="settings-row">
        <div className="setting-label">Font Size</div>
        <div className="setting-control">
          <div style={{ display: 'flex', gap: '10px' }}>
            {fontSizeOptions.map(option => (
              <button 
                key={option.id}
                onClick={() => handleDisplaySettings('fontSize', option.id)}
                style={{
                  padding: '8px 15px',
                  backgroundColor: settings.display.fontSize === option.id ? 
                    settings.personalize.accentColor : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="settings-row">
        <div className="setting-label">Animations</div>
        <div className="setting-control">
          <label className="toggle-switch">
            <input 
              type="checkbox" 
              checked={settings.display.animation} 
              onChange={(e) => handleDisplaySettings('animation', e.target.checked)}
            />
            <span className="toggle-slider" style={{ backgroundColor: settings.display.animation ? settings.personalize.accentColor : '#666' }}></span>
          </label>
        </div>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
        <h3 style={{ marginTop: '0', marginBottom: '10px', fontSize: '16px' }}>Display Preview</h3>
        <div style={{ 
          padding: '20px', 
          backgroundColor: settings.display.darkMode ? '#111' : '#f5f5f5',
          color: settings.display.darkMode ? '#fff' : '#333',
          borderRadius: '5px',
          border: '1px solid ' + (settings.display.darkMode ? '#333' : '#ddd'),
          fontSize: 
            settings.display.fontSize === 'small' ? '12px' : 
            settings.display.fontSize === 'large' ? '16px' : '14px',
          transition: settings.display.animation ? 'all 0.3s ease' : 'none',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          opacity: settings.display.brightness / 100
        }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            Sample Text with {settings.display.fontSize} font
          </div>
          <div>
            This is a preview of how your display settings will look.
            <br/>
            Brightness: {settings.display.brightness}%<br/>
            Dark Mode: {settings.display.darkMode ? 'On' : 'Off'}<br/>
            Animations: {settings.display.animation ? 'On' : 'Off'}
          </div>
          <button 
            style={{
              marginTop: '15px',
              padding: '8px 15px',
              backgroundColor: settings.personalize.accentColor,
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: settings.display.animation ? 'all 0.3s ease' : 'none'
            }}
          >
            Sample Button
          </button>
        </div>
      </div>
    </div>
  );

  const renderPersonalizeSettings = () => (
    <div className="settings-content">
      <h2>Personalization</h2>
      <p>Make WebOS your own with these personalization options</p>
      
      <div className="settings-row">
        <div className="setting-label">Accent Color</div>
        <div className="setting-control">
          <div style={{ display: 'flex', gap: '10px' }}>
            {colorOptions.map(option => (
              <button 
                key={option.id}
                onClick={() => handlePersonalizeSettings('accentColor', option.id)}
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: option.color,
                  border: settings.personalize.accentColor === option.id ? 
                    '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => {
                  if (settings.display.animation) {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (settings.display.animation) {
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
      
      <div className="settings-row">
        <div className="setting-label">Wallpaper</div>
        <div className="setting-control">
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            {wallpaperOptions.map(option => (
              <div 
                key={option.id}
                onClick={() => handlePersonalizeSettings('wallpaper', option.id)}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  border: settings.personalize.wallpaper === option.id ? 
                    `2px solid ${settings.personalize.accentColor}` : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '120px',
                  height: '80px',
                  transition: 'transform 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                }}
                onMouseOver={(e) => {
                  if (settings.display.animation) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.5)';
                  }
                }}
                onMouseOut={(e) => {
                  if (settings.display.animation) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                  }
                }}
              >
                <img 
                  src={option.thumbnail} 
                  alt={option.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  padding: '4px 8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  fontSize: '12px',
                  textAlign: 'center',
                  backdropFilter: 'blur(4px)'
                }}>
                  {option.name}
                </div>
                {settings.personalize.wallpaper === option.id && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: settings.personalize.accentColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                  }}>
                    <i className="fas fa-check" style={{ fontSize: '10px', color: 'white' }}></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="settings-row">
        <div className="setting-label">Transparency Effects</div>
        <div className="setting-control">
          <label className="toggle-switch">
            <input 
              type="checkbox" 
              checked={settings.personalize.transparencyEffects} 
              onChange={(e) => handlePersonalizeSettings('transparencyEffects', e.target.checked)}
            />
            <span className="toggle-slider" style={{ backgroundColor: settings.personalize.transparencyEffects ? settings.personalize.accentColor : '#666' }}></span>
          </label>
        </div>
      </div>
      
      <div className="settings-row">
        <div className="setting-label">Rounded Corners</div>
        <div className="setting-control">
          <label className="toggle-switch">
            <input 
              type="checkbox" 
              checked={settings.personalize.roundedCorners} 
              onChange={(e) => handlePersonalizeSettings('roundedCorners', e.target.checked)}
            />
            <span className="toggle-slider" style={{ backgroundColor: settings.personalize.roundedCorners ? settings.personalize.accentColor : '#666' }}></span>
          </label>
        </div>
      </div>

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)' }}>
        <h3 style={{ marginTop: '0', marginBottom: '15px', fontSize: '18px' }}>Wallpaper Preview</h3>
        <div style={{ 
          position: 'relative',
          width: '100%',
          height: '200px',
          backgroundImage: `url(${wallpaperOptions.find(w => w.id === settings.personalize.wallpaper)?.path})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: settings.personalize.roundedCorners ? '12px' : '0',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)'
        }}>
          <div style={{
            position: 'absolute',
            bottom: '15px',
            left: '15px',
            right: '15px',
            height: '40px',
            backgroundColor: settings.personalize.transparencyEffects ? 
              'rgba(20, 20, 40, 0.6)' : 'rgba(30, 30, 50, 1)',
            backdropFilter: settings.personalize.transparencyEffects ? 'blur(10px)' : 'none',
            borderRadius: settings.personalize.roundedCorners ? '20px' : '0',
            display: 'flex',
            alignItems: 'center',
            padding: '0 15px',
            gap: '15px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              width: '15px',
              height: '15px',
              backgroundColor: settings.personalize.accentColor,
              borderRadius: '50%'
            }}></div>
            <div style={{
              width: '15px',
              height: '15px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%'
            }}></div>
            <div style={{
              width: '15px',
              height: '15px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%'
            }}></div>
            <div style={{ flex: 1 }}></div>
            <div style={{ color: 'white', fontSize: '12px' }}>12:00</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPrivacySettings = () => (
    <div className="settings-content">
      <h2>Privacy Settings</h2>
      <p>Manage your privacy and data settings</p>
      
      {/* Simple placeholder content for privacy section */}
      <div style={{ color: '#aaa', fontSize: '14px', marginTop: '20px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
        <i className="fas fa-lock" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
        <p>This section would contain privacy controls such as:</p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
          <li>Location services settings</li>
          <li>Data collection preferences</li>
          <li>Browsing history controls</li>
          <li>Cookie management</li>
        </ul>
        <p style={{ marginTop: '15px' }}>This is a placeholder since this is a simulated web OS.</p>
      </div>
    </div>
  );

  const renderAboutSettings = () => (
    <div className="settings-content">
      <h2>About This System</h2>
      <p>System information and details</p>
      
      <div style={{ marginTop: '20px' }}>
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '15px',
              backgroundColor: settings.personalize.accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              color: 'white'
            }}>
              <i className="fas fa-desktop"></i>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>WebTopOS</div>
              <div style={{ color: '#aaa', fontSize: '14px' }}>A web-based operating system simulation</div>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '5px' }}>Version</div>
              <div>{settings.about.version}</div>
            </div>
            <div>
              <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '5px' }}>Build</div>
              <div>{settings.about.buildNumber}</div>
            </div>
            <div>
              <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '5px' }}>Device</div>
              <div>{settings.about.deviceName}</div>
            </div>
            <div>
              <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '5px' }}>Storage</div>
              <div>{settings.about.storage.used} of {settings.about.storage.total}</div>
            </div>
          </div>
        </div>
        
        <div style={{ textAlign: 'center', color: '#aaa', fontSize: '14px', padding: '20px' }}>
          <p>Created for demonstration purposes</p>
          <p>© 2023 WebTopOS Team</p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      height: '100%', 
      width: '100%', 
      display: 'flex', 
      backgroundColor: 'black',
      color: 'white' 
    }}>
      {/* Sidebar */}
      <div style={{ 
        width: '220px', 
        height: '100%', 
        backgroundColor: '#111',
        borderRight: '1px solid #333',
        padding: '20px 0',
        overflowY: 'auto'
      }}>
        <div style={{ padding: '0 20px 20px', borderBottom: '1px solid #333', marginBottom: '15px' }}>
          <h1 style={{ fontSize: '20px', margin: '0 0 5px' }}>Settings</h1>
          <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Configure your system</p>
        </div>
        
        <ul style={{ 
          listStyle: 'none', 
          padding: 0, 
          margin: 0 
        }}>
          {menuItems.map(item => (
            <li 
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              style={{
                padding: '12px 20px',
                cursor: 'pointer',
                backgroundColor: activeSection === item.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                borderLeft: activeSection === item.id ? `3px solid ${settings.personalize.accentColor}` : '3px solid transparent',
                transition: settings.display.animation ? 'all 0.2s' : 'none'
              }}
              onMouseOver={(e) => {
                if (activeSection !== item.id && settings.display.animation) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }
              }}
              onMouseOut={(e) => {
                if (activeSection !== item.id && settings.display.animation) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <i className={`fas fa-${item.icon}`} style={{ 
                  width: '20px', 
                  color: activeSection === item.id ? settings.personalize.accentColor : '#aaa',
                  textAlign: 'center' 
                }}></i>
                <span>{item.label}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Content */}
      <div style={{ 
        flex: 1, 
        padding: '30px', 
        overflowY: 'auto'
      }}>
        {activeSection === 'display' && renderDisplaySettings()}
        {activeSection === 'personalize' && renderPersonalizeSettings()}
        {activeSection === 'privacy' && renderPrivacySettings()}
        {activeSection === 'about' && renderAboutSettings()}
      </div>
      
      {/* Styles */}
      <style>
        {`
          .settings-row {
            margin-bottom: 20px;
            padding-bottom: 20px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            align-items: center;
          }
          
          .setting-label {
            width: 180px;
            font-weight: bold;
          }
          
          .setting-control {
            flex: 1;
          }
          
          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 50px;
            height: 24px;
          }
          
          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }
          
          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #666;
            transition: .4s;
            border-radius: 24px;
          }
          
          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
          }
          
          input:checked + .toggle-slider:before {
            transform: translateX(26px);
          }
          
          .settings-content h2 {
            margin-top: 0;
            margin-bottom: 5px;
            font-size: 24px;
          }
          
          .settings-content p {
            margin-top: 0;
            margin-bottom: 25px;
            color: #aaa;
          }
        `}
      </style>
    </div>
  );
};

export default Settings; 